import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import moment from 'moment-timezone';
import EditShiftModal from './EditShiftModal';
import ViewNotesModal from './ViewNotesModal';
import Images from '../../../Utils/images';
import Accordion from '@mui/material/Accordion';
import { Grid, IconButton } from '@mui/material';
import CustomButton from '../../../Components/CustomButton';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { getShiftPreviewDataStart } from '../../../redux/orderSlice';
import { RootState, useAppSelector } from '../../../redux/store';
import {
  formatPriceBreakupValues,
  getEndDayTimestamp,
  getStartDayTimestamp,
  getTimeWithTimeZoneAbbreviationNew,
} from '../../../Utils/helperFns';
import AdditionalShifts from './AdditionalShifts';
import AdditionalChargesModal from '../../Cart/Components/AdditionalChargesModal';
import ToolTipComponent from '../../../Components/ToolTipComponent';
import { Worksheet } from '../../MyOrders/Components/ShiftDetails';

const SHIFT_DETAILS_CONSOLIDATED_PRICE_TOOLTIP =
  'This consolidated price includes the daily, hourly, and extra-day rates for the shift.';
const SHIFT_DETAILS_CONSOLIDATED_HOURS_TOOLTIP =
  'The consolidated hours does not include fixed cost shifts.';

// Types for better TypeScript support
interface CartDetails {
  title?: string;
  totalCost?: number;
  totalHours?: number;
  cartId?: string;
  endTime?: any;
  startTime?: any;
}

interface Shift {
  _id: string;
  actualStartTime: string;
  actualEndTime: string;
  duration: number;
  /** Backend sometimes marks flexible-hour shifts at shift-level */
  isFLexibleShiftHr?: boolean;
  deliveryAddress: {
    locationName: string;
    locationPurpose: string[];
    address: string;
    locationId: string;
    setId: string;
  };
  unit?: number;
  cost?: number;
  extraDayPrice?: number;
  isWeeklyShift?: boolean;
  mileageCost?: number;
  mileageDistance?: number;
  mileageRate?: number;
  perDiemCost?: number;
  applyPerDiem?: boolean;
  rushFee?: number;
  rushFeeApplied?: boolean;
}

interface CartData {
  _id: string;
  worksheets: Worksheet[];
}

interface ShitDetailsProps {
  cartDetails: CartDetails;
  cartData: CartData[];
  totalWorksheets: number;
  isEditMode?: boolean;
  setIsEditMode: (isEditMode: boolean) => void;
  setCheckbox: React.Dispatch<React.SetStateAction<any[]>>;
  setSelectedDate: (date: any) => void;
  onDeleteShiftsClick?: () => void;
  totalShifts?: number;
}

// Constants
const ITEMS_PER_PAGE = 10;
const DEBOUNCE_DELAY = 300;
const SCROLL_THRESHOLD = 50;
const MAX_HEIGHT = '550px';

// Date formatting for service date
const formatServiceDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Pure function of `worksheet` — safe at module scope
const getWorksheetTotalCost = (worksheet: any) => {
  const shifts = worksheet?.shifts ?? [];
  if (shifts.length === 0) return Number(worksheet?.totalCost) || 0;
  return shifts.reduce(
    (sum: number, s: any) =>
      sum +
      (s.isWeeklyShift
        ? 0
        : Number(s?.cost || s?.extraDayPrice || 0) +
          (s?.mileageCost ?? 0) +
          (s?.applyPerDiem ? s?.perDiemCost ?? 0 : 0) +
          (s?.rushFee ?? 0)),
    0,
  );
};

interface WorksheetItemProps {
  worksheet: Worksheet;
  expandedAccordion: string | null;
  isEditMode?: boolean;
  selectedShiftIds: Set<string>;
  filteredShifts: (data: Worksheet[]) => Shift[];
  handleWorksheetSelection: (worksheetId: string, checked: boolean) => void;
  handleToggle: (id: string) => void;
  handleShiftSelection: (
    shiftId: string,
    checked: boolean,
    worksheetId: string,
  ) => void;
  handleNotesModalOpen: (shift: any) => void;
  setAdditionalShifts: React.Dispatch<React.SetStateAction<boolean>>;
  setAdditionalShiftsData: React.Dispatch<React.SetStateAction<any>>;
}

const WorksheetItem = React.memo(
  ({
    worksheet,
    expandedAccordion,
    isEditMode,
    selectedShiftIds,
    filteredShifts,
    handleWorksheetSelection,
    handleToggle,
    handleShiftSelection,
    handleNotesModalOpen,
    setAdditionalShifts,
    setAdditionalShiftsData,
  }: WorksheetItemProps) => (
    <Accordion
      key={worksheet._id}
      expanded={expandedAccordion === worksheet._id}
      className="ScheduleAccordian"
    >
      <AccordionSummary
        onClick={event => event.stopPropagation()}
        className="ScheduleSummary"
      >
        <div className="serviceCard">
          <figure className="flex ">
            {isEditMode &&
              moment().isSameOrBefore(moment(worksheet?.lastShift)) && (
                <div className="checkbox">
                  <input
                    type="checkbox"
                    checked={
                      Array.from(selectedShiftIds)?.filter((val: string) =>
                        worksheet?.shifts?.find(
                          (shift: Shift) => shift._id === val,
                        ),
                      )?.length >= filteredShifts([worksheet])?.length
                    }
                    onChange={e =>
                      handleWorksheetSelection(worksheet._id, e.target.checked)
                    }
                    ref={el => {
                      if (el) {
                        el.indeterminate =
                          Array.from(selectedShiftIds)?.filter((val: string) =>
                            worksheet?.shifts?.find(
                              (shift: Shift) => shift._id === val,
                            ),
                          )?.length < filteredShifts([worksheet])?.length &&
                          Array.from(selectedShiftIds)?.filter((val: string) =>
                            worksheet?.shifts?.find(
                              (shift: Shift) => shift._id === val,
                            ),
                          )?.length > 0;
                      }
                    }}
                    onClick={e => e.stopPropagation()}
                  />
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label aria-label="Select worksheet"></label>
                </div>
              )}
            <img
              src={worksheet.productInfo?.thumbnailImg || Images.AUTH_BG_1}
              alt={worksheet.productInfo?.title || 'Service'}
              loading="lazy"
            />
          </figure>
          <div className="serviceDetails">
            <div className="serviceInfo">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '13px',
                }}
              >
                <h2>{worksheet.productInfo?.title || 'Service'}</h2>
                {worksheet?.rentPricing?.isFlexibleShiftHr && (
                  <div className="chips">
                    <span className="dark-blue">
                      {worksheet?.rentPricing?.minRateModel == 'DAILY'
                        ? 'Daily'
                        : 'Weekly'}
                    </span>
                  </div>
                )}
              </div>
              <span>
                <i className="pin-icon mr-6"></i>
                {worksheet?.locationPurpose?.[0]}
                {worksheet?.locationPurpose?.length > 1 && (
                  <ToolTipComponent
                    theme="dark"
                    title={
                      <div>
                        {worksheet?.locationPurpose.map(
                          (purpose: string, index: number) => {
                            if (index == 0) return null;
                            return <div key={index}>{purpose}</div>;
                          },
                        )}
                      </div>
                    }
                  >
                    <span className="cursor-pointer">
                      + {worksheet?.locationPurpose?.length - 1}
                    </span>
                  </ToolTipComponent>
                )}
                {' :'}{' '}
                {`${worksheet?.locationName + ', ' + worksheet?.address}`} |{' '}
                {worksheet?.rentPricing?.isFlexibleShiftHr ? (
                  'Flexible'
                ) : (
                  <span>
                    {moment(worksheet.firstShift).format('h:mm A')}{' '}
                    {getTimeWithTimeZoneAbbreviationNew(
                      worksheet.firstShift,
                      'z',
                    )}{' '}
                    - {moment(worksheet.lastShift).format('h:mm A')}{' '}
                    {getTimeWithTimeZoneAbbreviationNew(
                      worksheet.lastShift,
                      'z',
                    )}
                  </span>
                )}
              </span>
            </div>
            <div className="serviceDates">
              <ul>
                <li>
                  <h3 className="mb6">
                    {' '}
                    {worksheet.totalUnit > 1 ? 'Units' : 'Unit'}
                  </h3>
                  <p className="fw600 MuiTypography-body2">
                    {worksheet.totalUnit}
                  </p>
                </li>
                <li>
                  <h3 className="mb6">Duration</h3>
                  <p className="fw600 MuiTypography-body2">
                    {worksheet?.rentPricing?.isFlexibleShiftHr
                      ? 'Flexible'
                      : worksheet?.totalDuration + 'hrs'}
                  </p>
                </li>
                <li>
                  <h3 className="mb6">Cost</h3>
                  <p className="txt_primary MuiTypography-body2 d-flex align-center">
                    {worksheet.shifts?.length > 0 &&
                    worksheet.shifts?.every((s: any) => s.isWeeklyShift) ? (
                      <span style={{ color: 'green' }}>Included</span>
                    ) : (
                      <>
                        $
                        {formatPriceBreakupValues(
                          getWorksheetTotalCost(worksheet),
                        )}
                      </>
                    )}
                  </p>
                </li>
              </ul>

              <IconButton onClick={() => handleToggle(worksheet._id)}>
                <img
                  src={
                    expandedAccordion === worksheet._id
                      ? Images.Arrow_Up
                      : Images.ARROW_DOWN
                  }
                  alt="toggle"
                />
              </IconButton>
            </div>
          </div>
        </div>
      </AccordionSummary>

      <AccordionDetails className="preview-details">
        {worksheet.shifts?.map((shift: Shift) => (
          <div key={shift._id} className="locations">
            <div className="serviceCard">
              <div className="check-div">
                {isEditMode &&
                  moment().isSameOrBefore(moment(shift.actualEndTime)) && (
                    <>
                      <div className="checkbox">
                        <input
                          type="checkbox"
                          checked={selectedShiftIds.has(shift._id)}
                          onChange={e =>
                            handleShiftSelection(
                              shift._id,
                              e.target.checked,
                              worksheet._id,
                            )
                          }
                          onClick={e => e.stopPropagation()}
                        />
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label aria-label="Select shift"></label>
                      </div>
                    </>
                  )}
              </div>
              <div className="preview-details-row">
                <Grid container spacing={1} className=" mb-12">
                  <Grid item md={10} xs={12} className="">
                    <h3 className="MuiTypography-body2 ">
                      <p>
                        <i className="pin-icon mr-6"></i>
                        {shift?.deliveryAddress?.locationPurpose?.[0]}
                        {worksheet?.locationPurpose?.length > 1 && (
                          <ToolTipComponent
                            theme="light"
                            title={
                              <div>
                                {shift?.deliveryAddress?.locationPurpose?.map(
                                  (purpose: string, index: number) => {
                                    if (index == 0) return null;
                                    return <div key={index}>{purpose}</div>;
                                  },
                                )}
                              </div>
                            }
                          >
                            <span className="cursor-pointer">
                              {' '}
                              +{' '}
                              {shift?.deliveryAddress?.locationPurpose?.length -
                                1}
                            </span>
                          </ToolTipComponent>
                        )}
                        {' :'}{' '}
                        {(worksheet?.locationName + worksheet?.address)
                          ?.length > 100
                          ? `${(
                              worksheet?.locationName +
                              ', ' +
                              worksheet?.address
                            )?.substring(0, 100)}...`
                          : `${
                              worksheet?.locationName +
                              ', ' +
                              worksheet?.address
                            }`}{' '}
                        |{' '}
                        {worksheet?.rentPricing?.isFlexibleShiftHr ? (
                          'Flexible'
                        ) : (
                          <span>
                            {moment(shift?.actualStartTime).format('h:mm A')}{' '}
                            {getTimeWithTimeZoneAbbreviationNew(
                              shift?.actualStartTime,
                              'z',
                            )}{' '}
                            - {moment(shift?.actualEndTime).format('h:mm A')}{' '}
                            {getTimeWithTimeZoneAbbreviationNew(
                              shift?.actualEndTime,
                              'z',
                            )}
                          </span>
                        )}
                      </p>
                    </h3>
                  </Grid>

                  <Grid item md={2} xs={12} className="text-md-right">
                    <button
                      onClick={() => handleNotesModalOpen(shift)}
                      className="text-linking MuiTypography-body2 link-btn"
                    >
                      View Notes
                    </button>
                  </Grid>
                </Grid>

                <div className="serviceDates">
                  <ul>
                    <li>
                      <h3 className="mb6">Status</h3>
                      <p className="fw600 MuiTypography-body2">NA</p>
                    </li>
                    <li>
                      <h3 className="mb6">Duration</h3>
                      <p className="fw600 MuiTypography-body2">
                        {worksheet?.rentPricing?.isFlexibleShiftHr
                          ? 'Flexible'
                          : shift?.duration + 'hrs'}
                      </p>
                    </li>
                    <li>
                      <h3 className="mb6">Cost</h3>
                      <p className="txt_primary MuiTypography-body2 d-flex align-center">
                        {shift.isWeeklyShift ? (
                          <>
                            <span style={{ color: 'black' }}>Included</span>
                            <i
                              className="info-icon ml-6 cursor-pointer"
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                setAdditionalShifts(true);
                                setAdditionalShiftsData(shift);
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setAdditionalShifts(true);
                                  setAdditionalShiftsData(shift);
                                }
                              }}
                              aria-label="View additional charges"
                            />
                          </>
                        ) : (
                          <>
                            $
                            {formatPriceBreakupValues(
                              Number(shift?.cost || shift?.extraDayPrice || 0) +
                                (shift?.mileageCost ?? 0) +
                                (shift?.applyPerDiem
                                  ? shift?.perDiemCost ?? 0
                                  : 0) +
                                (shift?.rushFee ?? 0),
                            )}{' '}
                            &nbsp;
                            <i
                              className="info-icon cursor-pointer"
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                setAdditionalShifts(true);
                                setAdditionalShiftsData(shift);
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setAdditionalShifts(true);
                                  setAdditionalShiftsData(shift);
                                }
                              }}
                              aria-label="View additional charges"
                            />
                          </>
                        )}
                      </p>
                    </li>
                  </ul>
                </div>

                {/* <p className="text-warning_500 MuiTypography-body2 d-flex align-center">
                  <i className="info-warning-icon mr-6"></i> Approval pending
                  from vendor
                </p> */}
              </div>
            </div>
          </div>
        ))}
      </AccordionDetails>
    </Accordion>
  ),
);
WorksheetItem.displayName = 'WorksheetItem';

export default function ShiftDetails({
  cartDetails,
  cartData,
  totalWorksheets,
  isEditMode,
  setIsEditMode,
  onDeleteShiftsClick,
  totalShifts = 0,
  setCheckbox,
  setSelectedDate,
}: ShitDetailsProps) {
  // State management
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(
    null,
  );
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);

  // Modal and selection state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWorksheet, setSelectedWorksheet] = useState<Worksheet | null>(
    null,
  );
  const [selectedShifts, setSelectedShifts] = useState<Shift[]>([]);
  const [selectedWorksheets, _setSelectedWorksheets] = useState<Set<string>>(
    new Set(),
  );
  const [selectedShiftIds, setSelectedShiftIds] = useState<Set<string>>(
    new Set(),
  );
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [selectedWorksheetForNotes, setSelectedWorksheetForNotes] =
    useState<Worksheet | null>(null);

  const [editCostState, setEditCostState] = useState({
    editGrandTotalCost: 0,
    editGrandTotalHours: 0,
  });

  const [additionalShifts, setAdditionalShifts] = useState(false);
  const [additionalShiftsData, setAdditionalShiftsData] = useState<any>({});
  const [additionalChargesOpen, setAdditionalChargesOpen] = useState(false);
  const [additionalChargesShifts, _setAdditionalChargesShifts] = useState<
    any[]
  >([]);
  // Refs
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<any>(null);
  const worksheetsBeforeAppendRef = useRef<number | null>(null);
  const loadMoreLockRef = useRef(false);

  // Redux selectors - optimized
  const { shiftPreviewData, loader, shiftPreviewAppendEpoch } = useAppSelector(
    (state: RootState) => ({
      shiftPreviewData: state.order.shiftPreviewData,
      loader: state.order.loader,
      shiftPreviewAppendEpoch: state.order.shiftPreviewAppendEpoch,
    }),
    (left: any, right: any) =>
      left.shiftPreviewData === right.shiftPreviewData &&
      left.loader === right.loader &&
      left.shiftPreviewAppendEpoch === right.shiftPreviewAppendEpoch,
  );

  const { previewShiftDetails } = useAppSelector(
    (state: RootState) => state.common,
  );

  // Memoized computed values
  const displayData = useMemo(
    () => shiftPreviewData?.data?.data || cartData || [],
    [shiftPreviewData?.data?.data, cartData],
  );

  const totalWorksheetsFromApi = useMemo(
    () => shiftPreviewData?.data?.total || totalWorksheets || 0,
    [shiftPreviewData?.data?.total, totalWorksheets],
  );

  /** API-computed total cost across all worksheets in scope (not just the loaded page). */
  const apiTotalEstimateCost: number | undefined =
    shiftPreviewData?.data?.totalEstimateCost;

  /** API-computed total duration (hrs) across all worksheets in scope (not just the loaded page). */
  const apiTotalEstimateDuration: number | undefined =
    shiftPreviewData?.data?.totalEstimateDuration;

  const currentWorksheetCount = useMemo(
    () =>
      displayData.reduce(
        (total: number, cart: CartData) =>
          total + (cart?.worksheets?.length || 0),
        0,
      ),
    [displayData],
  );

  // Helper function to get all shift IDs from a worksheet
  const getWorksheetShiftIds = useCallback((worksheet: Worksheet): string[] => {
    return worksheet.shifts?.map((shift: Shift) => shift._id) || [];
  }, []);

  const findShiftById = useCallback(
    (shiftId: string, worksheetId: string): Shift | null => {
      for (const cart of displayData) {
        const worksheet = cart.worksheets?.find(
          (w: Worksheet) => w._id === worksheetId,
        );
        if (worksheet) {
          const shift = worksheet.shifts?.find((s: Shift) => s._id === shiftId);
          if (shift) return shift;
        }
      }
      return null;
    },
    [displayData],
  );

  // Memoized handlers
  const handleToggle = useCallback((id: string) => {
    setExpandedAccordion((prevId: any) => (prevId === id ? null : id));
  }, []);

  // Handle worksheet selection
  const handleWorksheetSelection = useCallback(
    (worksheetId: string, checked: boolean) => {
      let targetWorksheet: Worksheet | null = null;

      for (const cart of displayData) {
        const found = cart.worksheets?.find(
          (w: Worksheet) => w._id === worksheetId,
        );
        if (found) {
          targetWorksheet = found;
          break;
        }
      }

      if (!targetWorksheet) return;

      //  FILTER: remove shifts whose end time has passed
      const validShifts =
        targetWorksheet.shifts?.filter((shift: any) =>
          moment(shift.actualEndTime).isSameOrAfter(moment()),
        ) || [];

      // If no valid shifts → do nothing
      if (!validShifts.length) return;

      const shiftIds = validShifts.map((shift: any) => shift._id);

      setSelectedShiftIds(prev => {
        const newSet = new Set(prev);

        // ---------------- CHECKBOX UPDATE ----------------
        setCheckbox((prevCheckboxData: any) => {
          const updatedCheckboxData = [...prevCheckboxData];

          const idx = updatedCheckboxData.findIndex(
            entry => entry.itemId === targetWorksheet?.cartId,
          );

          if (checked) {
            if (idx !== -1) {
              const currentIds = new Set(updatedCheckboxData[idx].checkedIds);
              shiftIds.forEach((id: string) => currentIds.add(id));

              updatedCheckboxData[idx] = {
                itemId: targetWorksheet?.cartId,
                checkedIds: Array.from(currentIds),
              };
            } else {
              updatedCheckboxData.push({
                itemId: targetWorksheet?.cartId,
                checkedIds: [...shiftIds],
              });
            }
          } else {
            if (idx !== -1) {
              const filteredIds = updatedCheckboxData[idx].checkedIds.filter(
                (id: string) => !shiftIds.includes(id),
              );

              if (filteredIds.length > 0) {
                updatedCheckboxData[idx] = {
                  itemId: targetWorksheet?.cartId,
                  checkedIds: filteredIds,
                };
              } else {
                updatedCheckboxData.splice(idx, 1);
              }
            }
          }

          return updatedCheckboxData;
        });

        // ---------------- SELECTION LOGIC ----------------
        if (checked) {
          shiftIds.forEach((id: string) => newSet.add(id));

          const nonWeeklyHoursDelta = validShifts
            .filter((s: any) => !s.isWeeklyShift)
            .reduce((sum: number, s: any) => sum + Number(s?.duration ?? 0), 0);
          setEditCostState(prev => ({
            editGrandTotalCost:
              prev.editGrandTotalCost + getWorksheetTotalCost(targetWorksheet),
            editGrandTotalHours: !targetWorksheet?.rentPricing
              ?.isFlexibleShiftHr
              ? prev.editGrandTotalHours + nonWeeklyHoursDelta
              : prev.editGrandTotalHours,
          }));
        } else {
          shiftIds.forEach((id: string) => newSet.delete(id));

          const nonWeeklyHoursDelta = validShifts
            .filter((s: any) => !s.isWeeklyShift)
            .reduce((sum: number, s: any) => sum + Number(s?.duration ?? 0), 0);
          setEditCostState(prev => ({
            editGrandTotalCost:
              prev.editGrandTotalCost - getWorksheetTotalCost(targetWorksheet),
            editGrandTotalHours: !targetWorksheet?.rentPricing
              ?.isFlexibleShiftHr
              ? prev.editGrandTotalHours - nonWeeklyHoursDelta
              : prev.editGrandTotalHours,
          }));
        }

        return newSet;
      });
    },
    [displayData, getWorksheetShiftIds, selectedWorksheets],
  );

  function findWorksheetById(worksheetId: string) {
    // Search your displayData or source data for the correct worksheet object
    for (const cart of displayData) {
      const ws = cart.worksheets?.find((w: Worksheet) => w._id === worksheetId);
      if (ws) return ws;
    }
    return null;
  }

  // Handle shift selection
  const handleShiftSelection = useCallback(
    (shiftId: string, checked: boolean, worksheetId: string) => {
      setSelectedShiftIds(prev => {
        const newSet = new Set(prev);
        if (checked) {
          newSet.add(shiftId);
        } else {
          newSet.delete(shiftId);
        }
        return newSet;
      });
      const shiftToAdd = findShiftById(shiftId, worksheetId);
      const worksheet = findWorksheetById(worksheetId);
      const shiftCost = shiftToAdd?.isWeeklyShift
        ? 0
        : Number(shiftToAdd?.cost ?? shiftToAdd?.extraDayPrice ?? 0) +
          (shiftToAdd?.mileageCost ?? 0) +
          (shiftToAdd?.applyPerDiem ? shiftToAdd?.perDiemCost ?? 0 : 0) +
          (shiftToAdd?.rushFee ?? 0);
      const shiftHoursForTotal =
        !worksheet?.rentPricing?.isFlexibleShiftHr && !shiftToAdd?.isWeeklyShift
          ? Number(shiftToAdd?.duration ?? 0)
          : 0;
      if (checked)
        setEditCostState(prev => ({
          editGrandTotalCost: prev.editGrandTotalCost + shiftCost,
          editGrandTotalHours: prev.editGrandTotalHours + shiftHoursForTotal,
        }));
      else {
        setEditCostState(prev => ({
          editGrandTotalCost: prev.editGrandTotalCost - shiftCost,
          editGrandTotalHours: prev.editGrandTotalHours - shiftHoursForTotal,
        }));
      }
      setCheckbox(prev => {
        const updatedArray = [...prev];
        const worksheet = findWorksheetById(worksheetId);
        const idx = prev.findIndex(item => item.itemId === worksheet.cartId);
        if (checked) {
          if (idx === -1) {
            // Add new item with shiftId
            updatedArray.push({
              itemId: worksheet.cartId,
              checkedIds: [shiftId],
            });
          } else {
            // Append shiftId if not present
            const existing = updatedArray[idx];
            if (!existing.checkedIds.includes(shiftId)) {
              const updatedIds = [...existing.checkedIds, shiftId];
              updatedArray[idx] = { ...existing, checkedIds: updatedIds };
            }
          }
        } else {
          if (idx !== -1) {
            // Remove shiftId from checkedIds
            const existing = updatedArray[idx];
            const updatedIds = existing.checkedIds.filter(
              (id: string) => id !== shiftId,
            );
            if (updatedIds.length === 0) {
              // Remove entire item if no shifts checked
              updatedArray.splice(idx, 1);
            } else {
              updatedArray[idx] = { ...existing, checkedIds: updatedIds };
            }
          }
        }

        return updatedArray;
      });
    },

    [],
  );

  // Handle edit button click
  const handleEditClick = useCallback(() => {
    // if (selectedShiftIds.size === 0) {
    //   alert('Please select at least one shift to edit');
    //   return;
    // }

    // Collect all selected shifts from all worksheets
    const allSelectedShifts: Shift[] = [];
    let targetWorksheet: Worksheet | null = null;

    for (const cart of displayData) {
      for (const worksheet of cart.worksheets) {
        // Check for individually selected shifts
        const selectedShiftsFromWorksheet = worksheet.shifts.filter(
          (shift: Shift) => selectedShiftIds.has(shift._id),
        );
        if (selectedShiftsFromWorksheet.length > 0) {
          if (!targetWorksheet) {
            targetWorksheet = worksheet; // Use first worksheet with selected shifts as target
          }
          allSelectedShifts.push(...selectedShiftsFromWorksheet);
        }
      }
    }

    if (targetWorksheet && allSelectedShifts.length > 0) {
      setSelectedWorksheet(targetWorksheet);
      setSelectedShifts(allSelectedShifts);
      setIsModalOpen(true);
    }
  }, [selectedWorksheets, selectedShiftIds, displayData]);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedWorksheet(null);
    setSelectedShifts([]);
    setSelectedShiftIds(new Set());
    setEditCostState({
      editGrandTotalCost: 0,
      editGrandTotalHours: 0,
    });
    setCheckbox([]);
  }, []);

  // Handle notes modal open
  const handleNotesModalOpen = useCallback((shift: any) => {
    setSelectedWorksheetForNotes(shift?.notes || []);
    setIsNotesModalOpen(true);
  }, []);

  // Handle notes modal close
  const handleNotesModalClose = useCallback(() => {
    setIsNotesModalOpen(false);
    // setSelectedWorksheetForNotes(null);
  }, []);

  const buildApiQuery = useCallback(
    (nextPage: number): string => {
      return `limit=${ITEMS_PER_PAGE}&cartIds=${
        cartDetails?.cartId || '68973a05354621dc9cb5500e'
      }&pageNo=${nextPage}&toDate=${getEndDayTimestamp(
        cartDetails?.endTime,
      )}&fromDate=${getStartDayTimestamp(cartDetails?.startTime)}`;
    },

    [cartDetails?.cartId, cartDetails?.endTime, cartDetails?.startTime],
  );

  const loadMoreData = useCallback(() => {
    if (loadMoreLockRef.current || loading || !hasMore || loader) return;

    if (
      totalWorksheetsFromApi > 0 &&
      currentWorksheetCount >= totalWorksheetsFromApi
    ) {
      setHasMore(false);
      return;
    }

    loadMoreLockRef.current = true;
    worksheetsBeforeAppendRef.current = currentWorksheetCount;
    setLoading(true);
    const nextPage = page + 1;
    const query = buildApiQuery(nextPage);
    getShiftPreviewDataStart(query, true);
    setPage(nextPage);
  }, [
    loading,
    hasMore,
    loader,
    page,
    currentWorksheetCount,
    totalWorksheetsFromApi,
    buildApiQuery,
  ]);

  const handleScroll = useCallback(() => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for debouncing
    debounceTimeoutRef.current = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;

      if (scrollHeight - scrollTop <= clientHeight + SCROLL_THRESHOLD) {
        loadMoreData();
      }
    }, DEBOUNCE_DELAY);
  }, [loadMoreData]);

  // Effects — reset pagination when preview scope changes
  useEffect(() => {
    setPage(1);
    worksheetsBeforeAppendRef.current = null;
    loadMoreLockRef.current = false;
    setHasMore(true);
    setLoading(false);
  }, [cartDetails?.cartId, cartDetails?.startTime, cartDetails?.endTime]);

  useEffect(() => {
    if (cartData == null) return;
    if (totalWorksheetsFromApi > 0) {
      setHasMore(currentWorksheetCount < totalWorksheetsFromApi);
    } else {
      setHasMore(
        currentWorksheetCount > 0 && currentWorksheetCount >= ITEMS_PER_PAGE,
      );
    }
  }, [cartData, currentWorksheetCount, totalWorksheetsFromApi]);

  useEffect(() => {
    if (worksheetsBeforeAppendRef.current === null) return;
    const before = worksheetsBeforeAppendRef.current;
    worksheetsBeforeAppendRef.current = null;
    loadMoreLockRef.current = false;
    setLoading(false);
    if (page > 1 && currentWorksheetCount <= before) {
      setHasMore(false);
    }
  }, [shiftPreviewData, page, currentWorksheetCount]);

  useEffect(() => {
    if (worksheetsBeforeAppendRef.current === null) return;
    loadMoreLockRef.current = false;
    setLoading(false);
    worksheetsBeforeAppendRef.current = null;
    setPage(p => Math.max(1, p - 1));
  }, [shiftPreviewAppendEpoch]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !hasMore || loading || loader || !displayData?.length) {
      return;
    }
    if (el.scrollHeight <= el.clientHeight + SCROLL_THRESHOLD) {
      loadMoreData();
    }
  }, [displayData, hasMore, loading, loader, loadMoreData]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  /** Hours from daily/hourly shifts only (excludes weekly), aligned with consolidated cost. */
  const getWorksheetNonWeeklyDurationHours = (worksheet: any) => {
    const shifts = worksheet?.shifts ?? [];
    return shifts.reduce(
      (sum: number, s: any) =>
        sum + (s.isWeeklyShift ? 0 : Number(s?.duration ?? 0)),
      0,
    );
  };

  const { grandTotalCost, grandTotalHours } = useMemo(() => {
    let grandTotalCost = 0;
    let grandTotalHours = 0;
    const allWorksheets: any[] = [];

    displayData.forEach((cart: any) => {
      if (Array.isArray(cart.worksheets)) {
        cart.worksheets.forEach((worksheet: any) => {
          allWorksheets.push(worksheet);
          const worksheetTotalCost = getWorksheetTotalCost(worksheet);
          grandTotalCost += worksheetTotalCost;

          if (!worksheet.rentPricing?.isFlexibleShiftHr) {
            grandTotalHours += getWorksheetNonWeeklyDurationHours(worksheet);
          }
        });
      }
    });

    return { grandTotalCost, grandTotalHours };
  }, [displayData]);

  /** Prefer the API's precomputed total; fall back to the client-side sum. */
  const displayGrandTotalCost =
    typeof apiTotalEstimateCost === 'number'
      ? apiTotalEstimateCost
      : grandTotalCost;

  /** Prefer the API's precomputed total; fall back to the client-side sum. */
  const displayGrandTotalHours =
    typeof apiTotalEstimateDuration === 'number'
      ? apiTotalEstimateDuration
      : grandTotalHours;

  const selectedWorksheetsForEdit = useMemo(() => {
    const worksheets: any[] = [];
    displayData.forEach((cart: any) => {
      (cart.worksheets || []).forEach((ws: any) => {
        if (ws.shifts?.some((s: any) => selectedShiftIds.has(s._id))) {
          worksheets.push(ws);
        }
      });
    });
    return worksheets;
  }, [displayData, selectedShiftIds]);
  const displayEditGrandTotalCost = useMemo(
    () => editCostState.editGrandTotalCost,
    [selectedWorksheetsForEdit, editCostState.editGrandTotalCost],
  );

  const flatWorksheets = useMemo(
    () => displayData.flatMap((c: CartData) => c.worksheets || []),
    [displayData],
  );
  /** Info icons only when flex+fixed and/or weekly+non-weekly coexist (view). */
  const showConsolidatedComboInfoIcons = useMemo(() => {
    if (!flatWorksheets.length) return false;
    const hasFlex = flatWorksheets.some(
      (w: Worksheet) => w.rentPricing?.isFlexibleShiftHr,
    );
    const hasFixed = flatWorksheets.some(
      (w: Worksheet) => !w.rentPricing?.isFlexibleShiftHr,
    );
    const hasWeeklyShift = flatWorksheets.some((w: Worksheet) =>
      w.shifts?.some((s: any) => s.isWeeklyShift),
    );
    const hasNonWeeklyShift = flatWorksheets.some((w: Worksheet) =>
      w.shifts?.some((s: any) => !s.isWeeklyShift),
    );
    return (hasFlex && hasFixed) || (hasWeeklyShift && hasNonWeeklyShift);
  }, [flatWorksheets]);

  /** Info icons only when selection mixes flex+fixed and/or weekly+non-weekly (edit). */
  const showEditComboInfoIcons = useMemo(() => {
    if (!selectedShiftIds.size) return false;
    let sawWeeklySel = false;
    let sawNonWeeklySel = false;
    const pricingKinds = new Set<'flex' | 'fixed'>();
    for (const cart of displayData) {
      for (const ws of cart.worksheets ?? []) {
        let hasSel = false;
        for (const s of ws.shifts ?? []) {
          if (!selectedShiftIds.has(s._id)) continue;
          hasSel = true;
          if ((s as any).isWeeklyShift) sawWeeklySel = true;
          else sawNonWeeklySel = true;
        }
        if (hasSel) {
          pricingKinds.add(
            ws.rentPricing?.isFlexibleShiftHr ? 'flex' : 'fixed',
          );
        }
      }
    }
    return (
      (sawWeeklySel && sawNonWeeklySel) ||
      (pricingKinds.has('flex') && pricingKinds.has('fixed'))
    );
  }, [displayData, selectedShiftIds]);

  const showConsolidatedHoursInfo = useMemo(() => {
    if (!flatWorksheets.length) return false;
    const hasFlex = flatWorksheets.some(
      (w: Worksheet) => w.rentPricing?.isFlexibleShiftHr,
    );
    const hasFixed = flatWorksheets.some(
      (w: Worksheet) => !w.rentPricing?.isFlexibleShiftHr,
    );
    const weeklyOnFixed = flatWorksheets.some(
      (w: Worksheet) =>
        !w.rentPricing?.isFlexibleShiftHr &&
        w.shifts?.some((s: any) => s.isWeeklyShift),
    );
    return (hasFlex && hasFixed) || weeklyOnFixed;
  }, [flatWorksheets]);

  /** Show "Flexible" label only when any shift/worksheet is truly flexible (view). */
  const hasAnyFlexibleShiftInView = useMemo(() => {
    return flatWorksheets.some(
      (w: any) =>
        Boolean(w?.rentPricing?.isFlexibleShiftHr) ||
        (w?.shifts ?? []).some((s: any) => Boolean(s?.isFLexibleShiftHr)),
    );
  }, [flatWorksheets]);

  /** Show "Flexible" label only when selection includes any flexible shift/worksheet (edit). */
  const hasAnyFlexibleShiftInEditSelection = useMemo(() => {
    if (!selectedShiftIds.size) return false;
    for (const cart of displayData) {
      for (const ws of cart.worksheets ?? []) {
        const wsIsSelected = (ws.shifts ?? []).some((s: any) =>
          selectedShiftIds.has(s._id),
        );
        if (!wsIsSelected) continue;
        if (ws?.rentPricing?.isFlexibleShiftHr) return true;
        for (const s of ws.shifts ?? []) {
          if (!selectedShiftIds.has(s._id)) continue;
          if (s?.isFLexibleShiftHr) return true;
        }
      }
    }
    return false;
  }, [displayData, selectedShiftIds]);

  const showEditConsolidatedHoursInfo = useMemo(() => {
    const wss = selectedWorksheetsForEdit;
    if (!wss?.length) return false;
    const hasFlex = wss.some(
      (w: Worksheet) => w.rentPricing?.isFlexibleShiftHr,
    );
    const hasFixed = wss.some(
      (w: Worksheet) => !w.rentPricing?.isFlexibleShiftHr,
    );
    const weeklyOnFixed = wss.some(
      (w: Worksheet) =>
        !w.rentPricing?.isFlexibleShiftHr &&
        w.shifts?.some((s: any) => s.isWeeklyShift),
    );
    return (hasFlex && hasFixed) || weeklyOnFixed;
  }, [selectedWorksheetsForEdit]);

  const allDisplayedShiftsAreWeekly = useMemo(() => {
    const shifts = flatWorksheets.flatMap((w: Worksheet) => w.shifts ?? []);
    if (!shifts.length) return false;
    return shifts.every((s: any) => s.isWeeklyShift);
  }, [flatWorksheets]);

  const editSelectionIncludesWeeklyShift = useMemo(() => {
    if (!selectedShiftIds.size) return false;
    for (const cart of displayData) {
      for (const ws of cart.worksheets ?? []) {
        for (const s of ws.shifts ?? []) {
          if (selectedShiftIds.has(s._id) && (s as any).isWeeklyShift) {
            return true;
          }
        }
      }
    }
    return false;
  }, [displayData, selectedShiftIds]);

  // Memoized components
  const LoadingIndicator = useMemo(
    () => (
      <div
        style={{
          textAlign: 'center',
          padding: '20px',
          color: '#666',
        }}
      >
        <p>Loading more shifts...</p>
      </div>
    ),
    [],
  );

  // removed console logs

  const showCheckBoxOnDate = (data: Worksheet[]) => {
    return (
      isEditMode &&
      data?.some(val => moment().isSameOrBefore(moment(val?.lastShift)))
    );
  };

  const handleDateSelection = (
    e: React.ChangeEvent<HTMLInputElement>,
    worksheet: Worksheet[],
  ) => {
    worksheet?.forEach(val => {
      if (e.target.checked) {
        handleWorksheetSelection(val?._id, true);
      } else {
        handleWorksheetSelection(val?._id, false);
      }
    });
  };

  const filteredShifts = useCallback(
    (data: Worksheet[]) => {
      const shifts: Shift[] = [];
      data?.map((worksheet: Worksheet) => {
        // Filter shifts to only future or ongoing ones
        const validShifts = worksheet.shifts?.filter((shift: Shift) =>
          moment(shift.actualEndTime).isSameOrAfter(moment()),
        );

        // Return worksheet only if it has valid shifts
        if (validShifts && validShifts.length > 0) {
          shifts.push(...validShifts);
        }

        return null;
      });

      return shifts;
    },
    [showCheckBoxOnDate, isEditMode],
  );
  console.log(previewShiftDetails, 'previewShiftDetails');
  return (
    <div className="ScheduledServices">
      <div className="ScheduledServicesHeading">
        {cartDetails && displayData?.length > 0 && (
          <div className="flex between">
            <div className="d-flex gap10">
              <h3 className="fw600 MuiTypography-h6 mb-6 flex align-center">
                {`${previewShiftDetails?.shiftTimes?.setName?.toUpperCase()}${
                  previewShiftDetails?.shiftTimes?.episodeName
                    ? ` || ${
                        previewShiftDetails?.shiftTimes?.episodeNumber &&
                        previewShiftDetails?.shiftTimes?.episodeNumber !==
                          'undefined'
                          ? `${previewShiftDetails?.shiftTimes?.episodeNumber} - `
                          : ''
                      }${previewShiftDetails?.shiftTimes?.episodeName}`
                    : ''
                }` || 'Shift Details'}
              </h3>

              <p className="MuiTypography-subtitle1 text-text">
                <i className="info-icon mr-4 mb2"></i>{' '}
                {'Full editing availability are available in the cart'}
              </p>
            </div>
            {displayData?.length > 0 &&
              (!isEditMode ? (
                <div>
                  <p className="MuiTypography-body2 fw600 tag-text d-inline-flex align-center flex-wrap">
                    <span className="text-gray_900 d-inline-flex align-center">
                      {displayGrandTotalCost > 0
                        ? `$${formatPriceBreakupValues(displayGrandTotalCost)}`
                        : allDisplayedShiftsAreWeekly
                        ? 'Included'
                        : `$${formatPriceBreakupValues(displayGrandTotalCost)}`}
                      {showConsolidatedComboInfoIcons ? (
                        <ToolTipComponent
                          title={SHIFT_DETAILS_CONSOLIDATED_PRICE_TOOLTIP}
                          arrow
                          theme="light"
                        >
                          <i
                            className="info-icon ml-6"
                            style={{ cursor: 'pointer' }}
                            aria-label="Consolidated price info"
                          />
                        </ToolTipComponent>
                      ) : null}
                    </span>{' '}
                    <span className="text-gray_900 d-inline-flex align-center">
                      {hasAnyFlexibleShiftInView
                        ? 'Flexible'
                        : `${displayGrandTotalHours}hrs`}
                      {showConsolidatedHoursInfo ? (
                        <ToolTipComponent
                          title={SHIFT_DETAILS_CONSOLIDATED_HOURS_TOOLTIP}
                          arrow
                          theme="light"
                        >
                          <i
                            className="info-icon ml-6"
                            style={{ cursor: 'pointer' }}
                            aria-label="Consolidated hours info"
                          />
                        </ToolTipComponent>
                      ) : null}
                    </span>
                  </p>
                </div>
              ) : (
                selectedShiftIds.size > 0 &&
                (displayEditGrandTotalCost > 0 ||
                  editSelectionIncludesWeeklyShift) && (
                  <div className="">
                    <p className="MuiTypography-body2 fw600 tag-text d-inline-flex align-center flex-wrap">
                      <span className="text-gray_900 d-inline-flex align-center">
                        {displayEditGrandTotalCost > 0
                          ? `$${formatPriceBreakupValues(
                              displayEditGrandTotalCost,
                            )}`
                          : 'Included'}
                        {showEditComboInfoIcons ? (
                          <ToolTipComponent
                            title={SHIFT_DETAILS_CONSOLIDATED_PRICE_TOOLTIP}
                            arrow
                            theme="light"
                          >
                            <i
                              className="info-icon ml-6"
                              style={{ cursor: 'pointer' }}
                              aria-label="Consolidated price info"
                            />
                          </ToolTipComponent>
                        ) : null}
                      </span>{' '}
                      <span className="text-gray_900 d-inline-flex align-center">
                        {hasAnyFlexibleShiftInEditSelection
                          ? 'Flexible'
                          : `${editCostState.editGrandTotalHours}hrs`}
                        {showEditConsolidatedHoursInfo ? (
                          <ToolTipComponent
                            title={SHIFT_DETAILS_CONSOLIDATED_HOURS_TOOLTIP}
                            arrow
                            theme="light"
                          >
                            <i
                              className="info-icon ml-6"
                              style={{ cursor: 'pointer' }}
                              aria-label="Consolidated hours info"
                            />
                          </ToolTipComponent>
                        ) : null}
                      </span>
                    </p>
                  </div>
                )
              ))}
          </div>
        )}
      </div>

      <div
        ref={scrollContainerRef}
        style={{ maxHeight: MAX_HEIGHT, overflowY: 'auto' }}
        className="shifts-scroll-container"
      >
        {displayData?.length > 0 &&
        displayData.find((val: CartData) => val.worksheets?.length > 0) ? (
          displayData.map((cart: CartData) => (
            <React.Fragment key={cart._id}>
              {cart.worksheets?.filter(val => val.shifts?.length > 0)?.length >
                0 && (
                <div className="serviceDate">
                  <p className="flex align-center">
                    {showCheckBoxOnDate(cart.worksheets) && (
                      <div className="checkbox">
                        <input
                          type="checkbox"
                          checked={
                            Array.from(selectedShiftIds)?.filter(
                              (val: string) =>
                                cart?.worksheets?.find(worksheet =>
                                  worksheet.shifts?.find(
                                    (shift: Shift) => shift._id === val,
                                  ),
                                ),
                            )?.length >=
                            filteredShifts(cart?.worksheets)?.length
                          }
                          onChange={e =>
                            handleDateSelection(e, cart?.worksheets)
                          }
                          onClick={e => e.stopPropagation()}
                          ref={el => {
                            if (el) {
                              el.indeterminate =
                                Array.from(selectedShiftIds)?.filter(
                                  (val: string) =>
                                    cart?.worksheets?.find(worksheet =>
                                      worksheet.shifts?.find(
                                        (shift: Shift) => shift._id === val,
                                      ),
                                    ),
                                )?.length <
                                  filteredShifts(cart?.worksheets)?.length &&
                                Array.from(selectedShiftIds)?.filter(
                                  (val: string) =>
                                    cart?.worksheets?.find(worksheet =>
                                      worksheet.shifts?.find(
                                        (shift: Shift) => shift._id === val,
                                      ),
                                    ),
                                )?.length > 0;
                            }
                          }}
                        />
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label aria-label="Select all shifts"></label>
                      </div>
                    )}
                    {formatServiceDate(cart.worksheets[0]?.firstShift)}
                  </p>
                </div>
              )}
              {cart.worksheets?.map((worksheet: Worksheet) => {
                if (!worksheet.shifts.length) return null;
                return (
                  <WorksheetItem
                    key={worksheet._id}
                    worksheet={worksheet}
                    expandedAccordion={expandedAccordion}
                    isEditMode={isEditMode}
                    selectedShiftIds={selectedShiftIds}
                    filteredShifts={filteredShifts}
                    handleWorksheetSelection={handleWorksheetSelection}
                    handleToggle={handleToggle}
                    handleShiftSelection={handleShiftSelection}
                    handleNotesModalOpen={handleNotesModalOpen}
                    setAdditionalShifts={setAdditionalShifts}
                    setAdditionalShiftsData={setAdditionalShiftsData}
                  />
                );
              })}
            </React.Fragment>
          ))
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            <h3>Result not found</h3>
            <p>
              We&apos;re sorry we couldn&apos;t find what you were looking for.
              Please try another keyword.
            </p>
          </div>
        )}

        {(loading || loader) && LoadingIndicator}
      </div>

      {isEditMode && displayData?.length > 0 && (
        <div className="cart-lower-btns btn-grp">
          <div>
            <CustomButton
              text="Cancel"
              variant="outlined"
              onClick={() => {
                setIsEditMode(false);
                setSelectedShiftIds(new Set());
                setCheckbox([]);
                setEditCostState({
                  editGrandTotalCost: 0,
                  editGrandTotalHours: 0,
                });
              }}
              showIcon={false}
              id="cancel-shift"
              size="large"
            />
          </div>
          {onDeleteShiftsClick != null && (
            <div>
              <CustomButton
                text={`Delete ${totalShifts > 1 ? 'Shifts' : 'Shift'}`}
                variant="contained"
                color="error"
                onClick={onDeleteShiftsClick}
                disabled={totalShifts === 0}
                showIcon={false}
                id="delete-shift"
                size="large"
              />
            </div>
          )}
          <div>
            <CustomButton
              text="Edit"
              size="large"
              variant="contained"
              onClick={handleEditClick}
              disabled={selectedShiftIds.size === 0}
              showIcon={false}
              id="save-shift"
            />
          </div>
        </div>
      )}

      {/* Edit Shift Modal */}
      <EditShiftModal
        open={isModalOpen}
        onClose={handleModalClose}
        selectedWorksheet={selectedWorksheet || undefined}
        selectedShifts={selectedShifts}
        cartDetails={cartDetails}
        buildApiQuery={buildApiQuery}
        setSelectedDate={setSelectedDate}
      />

      {/* View Notes Modal */}
      <ViewNotesModal
        open={isNotesModalOpen}
        onClose={handleNotesModalClose}
        notes={selectedWorksheetForNotes || []}
        worksheetTitle={
          selectedWorksheetForNotes?.productInfo?.title || 'Worksheet Notes'
        }
      />
      <AdditionalShifts
        open={additionalShifts}
        onClose={() => setAdditionalShifts(false)}
        additionalShiftsData={additionalShiftsData}
      />
      <AdditionalChargesModal
        open={additionalChargesOpen}
        onClose={() => setAdditionalChargesOpen(false)}
        shifts={additionalChargesShifts}
      />
    </div>
  );
}
